import styled, { css } from 'styled-components';

import Bullet from '../_Bullet';

export const StyledBullet = styled(Bullet)<{
  isSelected: boolean;
}>`
  position: absolute;
  top: 50%;
  left: 13px;
  transform: translate(0, -50%);
  z-index: 3;
  pointer-events: none;
  border-color: ${({ theme }) => theme.colors.primary};

  ${({ isSelected }) => isSelected && css`
    border-color: ${({ theme }) => theme.colors.gray14};
  `}
`;

type StyledToggleButtonProps = {
  leftPadding?: number;
  fullWidth?: boolean;
  readOnly?: boolean;
  isTouched?: boolean;
  isValid?: boolean;
  hasWarning?: boolean;
  hasCaution?: boolean;
  hasInfo?: boolean;
  disabled?: boolean;
};

export const StyledToggleButton = styled.span<StyledToggleButtonProps>`
  position: relative;
  display: inline-block;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme?.ToggleButton?.borderRadius};
  padding: ${({ leftPadding }) => `12px 13px 10px ${leftPadding}px`};
  box-sizing: border-box;
  min-height: 40px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;

  &:after {
    content: '';
    position: absolute;
    display: block;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: ${({ theme }) => theme?.ToggleButton?.borderRadius};
    border: 1px solid ${({ theme }) => theme.colors.primary};
    pointer-events: none;
  }

  ${({ fullWidth }) => fullWidth && css`
      width: 100%;
      text-align: center;
  `}

  input:not(:checked) ~ &.iA_Core_ToggleButton--selected {
    color: ${({ theme }) => theme.colors.gray14};

    &:after {
      border-color: ${({ theme }) => theme.colors.gray14};
    }
  }

  input:checked ~ & {
    z-index: 1;

    &:after {
      border-width: 2px;
    }
  }

  input:not(:disabled) ~ &:hover, input:not(:disabled):checked ~ &:hover {
    z-index: 2;
    color: ${({ theme }) => theme.colors.light};
    background: ${({ theme }) => theme.colors.white};

    &:after {
      border-color: ${({ theme }) => theme.colors.light};
      border-width: 2px;
    }
  }

  input:not(:disabled):active ~ &, input:not(:disabled):checked:active ~ & {
    color: ${({ theme }) => theme.colors.lighter};

    &:after {
      border-color: ${({ theme }) => theme.colors.lighter};
    }
  }

  input:checked:not(:disabled) ~ & {
    background: ${({ theme }) => theme.colors.blue12};
    color: ${({ theme }) => theme.colors.light};

    &:after {
      border-color: ${({ theme }) => theme.colors.light};
    }
  }

  input:disabled ~ & {
    font-style: ${({ theme }) => theme?.Button?.disabledFontStyle};
    cursor: default;

    &:after {
      border-color: ${({ theme }) => theme.colors.gray4};
    }
  }

  ${({ readOnly, disabled }) => disabled && !readOnly && css`
    color: ${({ theme }) => theme.colors.disabled};
  `}

  ${({ readOnly }) => readOnly && css`
    color: ${({ theme }) => theme.colors.black};
  `}

  input:not(:disabled):focus ~ & {
    box-shadow: 0 0 3px 0 ${({ theme }) => theme.colors.lighter};
    color: ${({ theme }) => theme.colors.lighter};

    &:after {
      border-color: ${({ theme }) => theme.colors.lighter};
    }
  }

  input:not(:disabled):focus:not(:focus-visible) ~ & {
    box-shadow: none;
    color: ${({ theme }) => theme.colors.primary};

    &:after {
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }

  input:checked:not(:disabled):focus:not(:focus-visible) ~ & {
    color: ${({ theme }) => theme.colors.light};

    &:after {
      border-color: ${({ theme }) => theme.colors.light};
    }
  }

  ${({ isValid, isTouched }) => !isValid && isTouched && css<StyledToggleButtonProps>`
    &:after {
      border-color: ${({ theme }) => theme.colors.error};
      border-width: 2px;
    }
  `}

  ${({ isValid, hasInfo, isTouched }) => isValid && hasInfo && isTouched && css<StyledToggleButtonProps>`
    &:after {
      border-color: ${({ theme }) => theme.colors.info};
      border-width: 2px;
    }
  `}

  ${({ isValid, hasCaution, isTouched }) => isValid && hasCaution && isTouched && css<StyledToggleButtonProps>`
    &:after {
      border-color: ${({ theme }) => theme.colors.caution};
      border-width: 2px;
    }
  `}

  ${({ isValid, hasWarning, isTouched }) => isValid && hasWarning && isTouched && css<StyledToggleButtonProps>`
    &:after {
      border-color: ${({ theme }) => theme.colors.warning};
      border-width: 2px;
    }
  `}
`;

type StyledLabelProps = {
  fullWidth?: boolean;
  isSwitch?: boolean;
  isValid?: boolean;
  hasWarning?: boolean;
  hasCaution?: boolean;
  hasInfo?: boolean;
  isTouched?: boolean;
  isChips?: boolean;
};

export const StyledLabel = styled.label<StyledLabelProps>`
  position: relative;
  margin-bottom: 16px;

  display: ${({ fullWidth }) => fullWidth ? 'flex' : 'inline-block'};
  ${({ fullWidth }) => fullWidth && css`
      flex-grow: 1;
      box-sizing: border-box;
  `}

  & input:checked:not(:disabled) ~ ${StyledBullet} {
    border-color: ${({ theme }) => theme.colors.light};

    &:after {
      background: ${({ theme }) => theme.colors.light};
    }
  }

  &:hover input:not(:disabled) ~ ${StyledBullet} {
    border-color: ${({ theme }) => theme.colors.light};
  }

  & input:not(:disabled):active ~ ${StyledBullet}, 
  & input:not(:disabled):focus ~ ${StyledBullet} {
    border-color: ${({ theme }) => theme.colors.lighter};
  }

  & input:not(:disabled):focus:not(:focus-visible) ~ ${StyledBullet} {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  & input:checked:not(:disabled):active ~ ${StyledBullet}, 
  & input:checked:not(:disabled):focus ~ ${StyledBullet} {
    border-color: ${({ theme }) => theme.colors.lighter};
    &:after {
      background: ${({ theme }) => theme.colors.lighter};
    }
  } 

  & input:checked:not(:disabled):focus:not(:focus-visible) ~ ${StyledBullet} {
    border-color: ${({ theme }) => theme.colors.light};
    &:after {
      background: ${({ theme }) => theme.colors.light};
    }
  }

  ${({ isSwitch, fullWidth }) => isSwitch && css<StyledLabelProps>`
    ${StyledToggleButton}:after {
      border-radius: 0;
    }

    &:first-child ${StyledToggleButton} {
      &:after {
        border-top-left-radius: ${({ theme }) => theme?.ToggleButton?.borderRadius};
        border-bottom-left-radius: ${({ theme }) => theme?.ToggleButton?.borderRadius};
      }
    }

    &:not(:last-child) ${StyledToggleButton} {
      margin-right: ${fullWidth ? '-2px' : '-1px'};
      &:after {
        margin-right: ${fullWidth ? '-2px' : '-1px'};
      }
    }

    &:last-child ${StyledToggleButton} {
      &:after {
        border-top-right-radius: ${({ theme }) => theme?.ToggleButton?.borderRadius};
        border-bottom-right-radius: ${({ theme }) => theme?.ToggleButton?.borderRadius};
      }
    }
  `}

  ${({ isChips }) => isChips && css<StyledLabelProps>`
    ${StyledToggleButton} {
      min-height: 30px;
      border-radius: 15px;
      padding: 8px 18px;
      font-size: 12px;

      &:after {
        border-radius: 15px;
      }
    }

    & input:checked ~ ${StyledToggleButton},
    & input:checked:not(:disabled) ~ ${StyledToggleButton}:hover,
    & input:checked:not(:disabled) ~ ${StyledToggleButton}:active { 
      padding: 8px 8px 8px 28px;
    }
  `}
`;

export const StyledHiddenRadioButton = styled.input`
  position: absolute;
  left: -1000000px;
`;

export const StyledRadioButtonText = styled.span`
  position: relative;
  top: -4px;
  display: inline-block;
  margin-left: 10px;
`;

export const StyledCheckIcon = styled.svg`
  display: none;
  position: absolute;
  top: 5px;
  left: 8px;
  width: 16px;
  height: 16px;

  input:checked ~ ${StyledToggleButton} & {
    display: inline-block;
  }
`;
